import axios from 'axios'
import { extractRequestId, recordApiFailure } from '../../features/work-items/clientDiagnostics'

// Production frontend is served by nginx on :3003 and proxies /api to the
// gateway. Falling back to a different origin breaks mutation requests with
// custom revision/idempotency headers at the browser CORS boundary.
const envApiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api/v1'
const API_BASE_URL = envApiUrl.startsWith('/')
  ? envApiUrl
  : `${envApiUrl}/api/v1`

export const DEFAULT_API_TIMEOUT_MS = 1_800_000

export const isLongRunningRequest = (config: any): boolean =>
  Boolean(
    config?.url?.includes('/sync/') ||
    config?.url?.includes('/prod-sync/') ||
    config?.url?.includes('/import/') ||
    // Загрузка макетов: набор в 280 МБ идёт минутами, и разлогин посреди неё
    // уводит страницу на /login вместе с запущенным мастером. Пусть ошибку
    // покажет сам мастер, а сессию рвёт что-нибудь короткое и однозначное.
    config?.url?.includes('/designs/uploads') ||
    config?.timeout > 300000,
  )

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: DEFAULT_API_TIMEOUT_MS, // 30 min for long-running operations
})

// ── Auth interceptor ────────────────────────────────────────────────
api.interceptors.request.use((config: any) => {
  const token = localStorage.getItem('auth-storage') ?? sessionStorage.getItem('auth-storage')
  if (token) {
    try {
      const authData = JSON.parse(token)
      if (authData.state?.token) {
        config.headers.Authorization = `Bearer ${authData.state.token}`
      }
    } catch {
      // corrupted storage – ignore
    }
  }
  return config
})

const MAX_RETRIES = 3
const retryDelays = [1000, 2000, 4000]

// ── Response interceptor ────────────────────────────────────────────
api.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    recordApiFailure({
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      requestId: extractRequestId(error.response?.headers, error.response?.data),
    })
    // Suppress 404 for media-mappings (normal when mapping not found)
    if (error.response?.status === 404 && error.config?.url?.includes('/media-mappings')) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      const isLongOp = isLongRunningRequest(error.config)

      if (!isLongOp) {
        localStorage.removeItem('auth-storage')
        sessionStorage.removeItem('auth-storage')
        window.location.href = '/login'
      }
    }

    // Auto-retry on 429
    if (error?.response?.status === 429) {
      const config = error.config
      const retryCount = config.__retryCount || 0

      if (retryCount < MAX_RETRIES) {
        config.__retryCount = retryCount + 1
        const delay = retryDelays[retryCount] || 4000
        console.warn(`[API] Rate limited, retry ${retryCount + 1}/${MAX_RETRIES} in ${delay}ms`)
        return new Promise((resolve) => {
          setTimeout(() => resolve(api(config)), delay)
        })
      }
    }

    return Promise.reject(error)
  },
)
